import DisclosureToggle from 'tio-ui/components/disclosure-toggle';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Card from 'tio-ui/components/card';
import { or } from 'tio-ui/utilities';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import { PhWarningCircle } from 'ember-phosphor-icons';
import IssueNotificationCard from 'tio-employee/components/slr/issue-notification-card';
import AddressSelectionCard from 'tio-employee/components/slr/address-selection-card';
import type {
  ContributionActionItem,
  ContributionActionTask,
} from 'tio-employee/services/reimagined-data';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

interface GroupedIssueNotificationCardSignature {
  Args: {
    item: ContributionActionItem;
    person: PersonModel;
    onAddressSubmit: (item: ContributionActionItem, addressId: string) => Promise<void>;
    /** Forwarded to each upload task's card — see IssueNotificationCard. */
    onUploadComplete?: () => void;
  };
  Element: HTMLDivElement;
}

export default class GroupedIssueNotificationCard extends Component<GroupedIssueNotificationCardSignature> {
  @tracked isExpanded = false;

  get tasks() {
    return this.args.item.tasks ?? [];
  }

  get issueLabels() {
    return [...new Set(this.tasks.flatMap((task) => task.issueLabels))];
  }

  getAddressOptions = (task: ContributionActionTask) => {
    const addresses = (task.metadata?.available_addresses ?? []) as Array<{
      id: string;
      label: string;
    }>;
    return addresses;
  };

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  handleAddressSubmit = (addressId: string) => {
    return this.args.onAddressSubmit(this.args.item, addressId);
  };

  <template>
    <Card
      @elevation="none"
      @padding="none"
      class="tio-stack-start gap-5 px-4 py-6"
      data-test-grouped-issue-notification-card
      ...attributes
    >
      <button
        type="button"
        class="tio-row-between w-full cursor-pointer"
        data-test-grouped-issue-toggle
        aria-expanded={{this.isExpanded}}
        aria-label={{if
          this.isExpanded
          (t "slr.reimagined.collapse_action_item")
          (t "slr.reimagined.expand_action_item")
        }}
        {{on "click" this.toggleExpanded}}
      >
        <div class="tio-row">
          <div class="tio-row-center rounded-lg shrink-0 size-12 tio-bg-warning-soft">
            <PhWarningCircle @size={{32}} class="tio-fg-warning" />
          </div>
          <div class="tio-stack-start gap-2 text-left">
            <div class="tio-row-tight">
              <Text @weight="semibold" class="tio-fg-secondary tracking-tight leading-snug">
                {{@item.title}}
              </Text>
              {{#if @item.badge}}
                <Chip @intent={{@item.badgeIntent}} @size="sm">
                  {{@item.badge}}
                </Chip>
              {{/if}}
            </div>
            <Text @weight="medium" class="tio-fg-muted leading-snug">
              {{@item.description}}
            </Text>
            {{#if this.issueLabels.length}}
              <div class="tio-slr-issue-labels">
                {{#each this.issueLabels as |label|}}
                  <Chip @intent="warning" @size="sm" data-test-grouped-issue-label>
                    {{label}}
                  </Chip>
                {{/each}}
              </div>
            {{/if}}
          </div>
        </div>
        <DisclosureToggle class="shrink-0 ml-4" @expanded={{this.isExpanded}} />
      </button>

      {{#if this.isExpanded}}
        <div
          class="tio-bg-info-soft tio-stack-start p-4 rounded-2xl w-full gap-4"
          data-test-grouped-issue-expanded
        >
          <Text class="tio-fg-secondary leading-relaxed">
            {{t "slr.reimagined.grouped_tasks_heading"}}
          </Text>

          <div class="tio-col-loose w-full">
            {{#each this.tasks as |task|}}
              <Card
                @elevation="none"
                @padding="sm"
                class="tio-col"
                data-test-grouped-issue-task={{task.id}}
              >
                <div class="tio-row-top">
                  <div
                    class="mt-0.5 size-5 rounded border-2 tio-border-warning tio-bg-warning-soft shrink-0"
                    aria-hidden="true"
                  ></div>
                  <div class="tio-col-tight min-w-0">
                    <Text @weight="semibold" class="leading-snug">
                      {{task.title}}
                    </Text>
                    <Text @size="sm" @weight="medium" class="tio-fg-muted leading-relaxed">
                      {{task.description}}
                    </Text>
                    {{#if task.issueLabels.length}}
                      <div class="tio-slr-issue-labels">
                        {{#each task.issueLabels as |label|}}
                          <Chip @intent="warning" @size="sm">
                            {{label}}
                          </Chip>
                        {{/each}}
                      </div>
                    {{/if}}
                  </div>
                </div>

                {{#if (eq task.resolutionType "upload")}}
                  <IssueNotificationCard
                    @title={{task.title}}
                    @description={{task.description}}
                    @person={{@person}}
                    @successMessage={{or task.successMessage @item.successMessage}}
                    @onUploadComplete={{@onUploadComplete}}
                    data-test-grouped-issue-upload-card
                  />
                {{else if (eq task.resolutionType "dropdown")}}
                  <AddressSelectionCard
                    @title={{task.title}}
                    @description={{task.description}}
                    @addresses={{this.getAddressOptions task}}
                    @onSubmit={{this.handleAddressSubmit}}
                    data-test-grouped-issue-address-card
                  />
                {{/if}}
              </Card>
            {{/each}}
          </div>
        </div>
      {{/if}}
    </Card>
  </template>
}
